import { Box, Chip, Typography } from '@mui/material';
import { BikeleasingConditionDTO, BikeleasingContractDetailsDTO, BikeleasingContractDetailsDTOStatusEnum } from 'probonio-shared-ui/api';
import { useBikeleasingPortalLinkBuilder } from 'probonio-shared-ui/utils/bikeleasingUrl';
import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { ExternalLinkButton } from '../../../component/button/ExternalLinkButton';
import { InfoAccordion } from '../../../component/infoAccordion/InfoAccordion';
import { InfoRow } from '../../../component/infoAccordion/InfoRow';
import { getBikeleasingEmployeeContractStatusColor } from '../util/getContractStatusColor';

interface PropsWithContractId {
  contractId: string;
}

const RequestEarlyBuyoutOffer: React.FC<PropsWithContractId> = ({ contractId }) => {
  const { t } = useTranslation('extensions');
  const buildBikeleasingPortalLink = useBikeleasingPortalLinkBuilder();
  return (
    <>
      <Typography key="requestEarlyBuyoutOfferInfo" sx={{ mx: 1, mb: 1, mt: 1 }}>
        {t('extensions:bikeleasing_v2.contract.requestEarlyBuyoutOfferInfo')}
      </Typography>
      <Box key="earlyBuyout" sx={{ mx: 1, mb: 1 }}>
        <ExternalLinkButton
          href={buildBikeleasingPortalLink(`/user/contracts/${contractId}?REQUEST_BUYOUT_OFFER=true`)}
          fullWidth
          sx={{ textAlign: 'center' }}
        >
          {t('bikeleasing_v2.contract.buttons.requestEarlyBuyoutOffer')}
        </ExternalLinkButton>
      </Box>
    </>
  );
};

const BikeTakeoverEndOfLease: React.FC<PropsWithContractId> = ({ contractId }) => {
  const { t } = useTranslation('extensions');
  const buildBikeleasingPortalLink = useBikeleasingPortalLinkBuilder();
  return (
    <Box sx={{ mx: 1 }}>
      <Typography sx={{ mb: 0.5, mt: 1 }}>{t('extensions:bikeleasing_v2.contract.bikeTakeoverEndOfLeaseInfo')}</Typography>
      <ExternalLinkButton
        href={buildBikeleasingPortalLink(`/user/contracts/${contractId}?REQUEST_BUYOUT_OFFER=true`)}
        fullWidth
        variant="contained"
        sx={{ mb: 1, mt: 1, textAlign: 'center' }}
      >
        {t('bikeleasing_v2.contract.buttons.bikeTakeoverEndOfLease')}
      </ExternalLinkButton>
    </Box>
  );
};

const TransmitBikeAcceptance: React.FC<PropsWithContractId> = ({ contractId }) => {
  const { t } = useTranslation('extensions');
  const buildBikeleasingPortalLink = useBikeleasingPortalLinkBuilder();
  return (
    <Box sx={{ mx: 1 }}>
      <Typography sx={{ mb: 0.5, mt: 1 }}>{t('extensions:bikeleasing_v2.contract.bikeAcceptanceInfo')}</Typography>
      <ExternalLinkButton
        href={buildBikeleasingPortalLink(`/user/contracts/${contractId}?TRANSMIT_BIKE_ACCEPTANCE=true`)}
        fullWidth
        variant="contained"
        sx={{ mb: 1, mt: 1, textAlign: 'center' }}
      >
        {t('bikeleasing_v2.contract.buttons.transmitBikeAcceptance')}
      </ExternalLinkButton>
    </Box>
  );
};

export const LeasingData: React.FC<{ contract: BikeleasingContractDetailsDTO; condition: BikeleasingConditionDTO }> = props => {
  const { t } = useTranslation('benefitModule');
  const { status, id, leasingDuration, leasingRate, leasingData, startDate, endDate } = props.contract;
  const { inspectionPackage } = props.condition;

  const renderDate = useCallback((value: string | undefined): string => (value ? t('common:date', { date: new Date(value) }) : ''), [t]);
  const renderEuro = useCallback(
    (value: number | undefined): string => (value !== undefined ? t('common:money', { money: value }) : ''),
    [t],
  );

  return (
    <InfoAccordion defaultExpanded title={t('extensions:bikeleasing_v2_benefit.section.leasing')}>
      {status === BikeleasingContractDetailsDTOStatusEnum.BikeShipped && (
        <TransmitBikeAcceptance key="transmitBikeAcceptance" contractId={id} />
      )}
      <InfoRow
        key="leasingStatus"
        label={t(`extensions:bikeleasing_v2.contract.status.label`)}
        info={
          <Chip
            color={getBikeleasingEmployeeContractStatusColor(status)}
            label={t(`extensions:bikeleasing_v2_benefit.contract.status.${status}`)}
          />
        }
      />
      <InfoRow
        key="leasingDuration"
        display={!!leasingDuration}
        label={t('extensions:bikeleasing_v2_benefit.contract.leasingDuration')}
        info={t('extensions:bikeleasing_v2_benefit.contract.leasingDurationMonths', { leasingDuration })}
      />
      <InfoRow key="startDate" label={t('extensions:bikeleasing_v2_benefit.contract.startDate')} info={renderDate(startDate)} />
      <InfoRow key="endDate" label={t('extensions:bikeleasing_v2_benefit.contract.endDate')} info={renderDate(endDate)} />
      <InfoRow
        key="leasingRate"
        label={t('extensions:bikeleasing_v2_benefit.contract.leasingRate')}
        info={renderEuro(leasingRate?.effectiveLeasingRate?.grossAmount)}
      />
      <InfoRow
        key="leasingRateInsuranceInclusive"
        label={t('extensions:bikeleasing_v2.contract.leasingRateInsuranceInclusive')}
        info={renderEuro(leasingData?.totalRateGrossNew?.amount ?? leasingData?.totalRateNew?.amount) || '-'}
      />
      <InfoRow
        key="RRPAccessoriesInclusive"
        label={t('extensions:bikeleasing_v2.contract.RRPAccessoriesInclusive')}
        info={renderEuro(leasingData?.purchasePrice?.amount) || '-'}
      />
      <InfoRow
        key="inspectionPackage"
        label={t('extensions:bikeleasing_v2_benefit.contract.insurance.inspectionPackage')}
        info={inspectionPackage?.conclusion ? t(`extensions:bikeleasing_v2_benefit.conclusion.${inspectionPackage.conclusion}`) : '-'}
      />
      {status === BikeleasingContractDetailsDTOStatusEnum.Expiring && (
        <BikeTakeoverEndOfLease key="bikeTakeoverEndOfLease" contractId={id} />
      )}
      {(status === BikeleasingContractDetailsDTOStatusEnum.Active || status === BikeleasingContractDetailsDTOStatusEnum.Expiring) && (
        <RequestEarlyBuyoutOffer contractId={id} key="earlyBuyoutOffer" />
      )}
    </InfoAccordion>
  );
};
